// §7.1 / §9.5 — **the single confinement function**.
//
// Every filesystem entry point calls `confine`. There is one implementation, one test suite and
// no per-call reimplementation, because a second copy of this logic is how one of them ends up
// subtly weaker than the other.
//
// The grammar itself is in `@hangar/shared` (§19.3 requires the server to apply exactly the same
// rules); this module owns *who may reach where*.
import {
  ROOT_APPS,
  ROOT_SYSTEM,
  appRoot,
  isUnder,
  normalizeVfsPath,
  userRoot,
  type PathError,
  type VfsPath,
} from '@hangar/shared';

export type VfsAccess = 'read' | 'write';

export type ConfineError = PathError | 'FORBIDDEN' | 'READ_ONLY';

export interface VfsCaller {
  /** The app asking. Its own `/Apps/<id>` is always reachable (§9.5). */
  appId: string;
  /** `guest` for an anonymous session, the user id otherwise (§7.1). */
  user: string;
  /**
   * Extra roots this caller holds — the file manager gets all of user space and `/Apps`
   * (§9.5), and §11 may name more for a specific built-in.
   */
  roots?: readonly VfsPath[];
  /**
   * Paths granted by a shell-drawn picker: per path, per session, and never inherited by a new
   * window (§9.5). A granted *directory* carries its subtree; a granted file does not.
   */
  grants?: Iterable<VfsPath>;
}

export type ConfineResult =
  | { ok: true; path: VfsPath }
  | { ok: false; error: ConfineError; path?: VfsPath };

/** The roots a caller may reach, most specific first. Exported so App Inspector can show them. */
export function rootsFor(caller: VfsCaller): VfsPath[] {
  return [appRoot(caller.appId), ...(caller.roots ?? [])];
}

/**
 * Resolve a path for a caller, or say precisely why not.
 *
 * `/System` is readable by everyone and writable by no one (§7.1). Everything else must sit
 * inside one of the caller's roots, or be covered by a picker grant.
 */
export function confine(caller: VfsCaller, input: string, access: VfsAccess): ConfineResult {
  const parsed = normalizeVfsPath(input);
  if (!parsed.ok) return { ok: false, error: parsed.error };
  const path = parsed.path;

  if (isUnder(path, ROOT_SYSTEM)) {
    return access === 'read' ? { ok: true, path } : { ok: false, error: 'READ_ONLY', path };
  }

  for (const root of rootsFor(caller)) {
    if (isUnder(path, root)) return { ok: true, path };
  }

  for (const grant of caller.grants ?? []) {
    // A grant covers the granted path itself and, for a directory, everything beneath it.
    if (isUnder(path, grant)) return { ok: true, path };
  }

  return { ok: false, error: 'FORBIDDEN', path };
}

/** Convenience for the many call sites that only need the path or a thrown error. */
export function confineOrThrow(caller: VfsCaller, input: string, access: VfsAccess): VfsPath {
  const result = confine(caller, input, access);
  if (result.ok) return result.path;
  throw new VfsConfinementError(result.error, input);
}

export class VfsConfinementError extends Error {
  readonly error: ConfineError;
  readonly requested: string;

  constructor(error: ConfineError, requested: string) {
    super(`vfs: ${error} for ${JSON.stringify(requested)}`);
    this.name = 'VfsConfinementError';
    this.error = error;
    this.requested = requested;
  }
}

/** The caller a built-in file manager is (§9.5): all of user space and `/Apps`. */
export function fileManagerCaller(appId: string, user: string): VfsCaller {
  return { appId, user, roots: [userRoot(user), ROOT_APPS] };
}

/** The caller the shell itself is: everything, so the picker can browse. */
export function shellCaller(user: string): VfsCaller {
  return { appId: 'hangar', user, roots: ['/'] };
}
